/**
 * Attendance — V0.3 daily attendance log for frequent visitors (domestic help).
 *
 * Admin sees society-wide attendance for a chosen date, KPI strip, FV-kind +
 * search filters, table of FV × day projection (first IN, last OUT, duration,
 * raw event count), and a "Export CSV (last 30 days)" download.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ClipboardCheck,
  Download,
  Filter,
  LogIn,
  LogOut,
  Users,
  CalendarDays,
  Search
} from 'lucide-react';
import { get, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate, formatDateTime, todayIsoDate } from '@/lib/format';

type FvKind =
  | 'maid'
  | 'cook'
  | 'driver'
  | 'nanny'
  | 'gardener'
  | 'laundry'
  | 'newspaper'
  | 'milk'
  | 'trainer'
  | 'other';

interface FvSummary {
  id: string;
  full_name: string;
  phone: string;
  kind: FvKind;
  status: string;
}

interface FvListResponse {
  items: FvSummary[];
  next_cursor: string | null;
}

interface AttendanceRow {
  id: string;
  society_id: string;
  frequent_visitor_id: string;
  unit_id: string | null;
  kind: 'in' | 'out';
  ts: string;
  date_local: string;
  guard_id: string | null;
  gate_id: string | null;
  notes: string | null;
  created_at: string;
}

interface AttendanceListResponse {
  items: AttendanceRow[];
  next_cursor: string | null;
}

interface ProjectedRow {
  fv_id: string;
  fv_name: string;
  fv_kind: FvKind;
  unit_label: string;
  first_in: string | null;
  last_out: string | null;
  duration_minutes: number | null;
  events: number;
}

const KIND_OPTIONS: { value: '' | FvKind; label: string }[] = [
  { value: '', label: 'All kinds' },
  { value: 'maid', label: 'Maid' },
  { value: 'cook', label: 'Cook' },
  { value: 'driver', label: 'Driver' },
  { value: 'nanny', label: 'Nanny' },
  { value: 'gardener', label: 'Gardener' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'newspaper', label: 'Newspaper' },
  { value: 'milk', label: 'Milk' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'other', label: 'Other' }
];

export function AttendancePage(): JSX.Element {
  const [date, setDate] = useState<string>(() => todayIsoDate());
  const [kind, setKind] = useState<'' | FvKind>('');
  const [search, setSearch] = useState<string>('');
  const [exporting, setExporting] = useState<boolean>(false);

  // Cursor pagination on the OUTER FV-list query so admins can page through
  // FVs even when N > page size. The attendance page is fan-out (one parallel
  // /attendance call per loaded FV) so paginating the inner per-FV event
  // streams would not match the page's day-view shape — see report for the
  // architectural rationale.
  const FV_PAGE_SIZE = 50;
  const filtersHash = 'verified-fvs';
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const fvList = useQuery({
    queryKey: ['attendance', 'fvs', cursor],
    queryFn: () =>
      get<FvListResponse>('/frequent-visitors', {
        params: { status: 'verified', limit: FV_PAGE_SIZE, ...(cursor ? { cursor } : {}) }
      }),
    staleTime: 60_000,
    placeholderData: (prev) => prev
  });
  const fvPaginator = useCursorAccumulator<FvSummary>(fvList.data, cursor, filtersHash);
  const fvItems = fvPaginator.items;
  const fvMap = useMemo(() => {
    const m = new Map<string, FvSummary>();
    for (const fv of fvItems) m.set(fv.id, fv);
    return m;
  }, [fvItems]);

  // Per-FV daily attendance. We fan out one summary call per FV against the
  // chosen day. The fan-out happens against the ACCUMULATED FV list, so
  // loading more FVs via the paginator widens the day-view's coverage.
  const fvIds = fvItems.map((f) => f.id);
  const dayEvents = useQuery({
    queryKey: ['attendance', 'day', date, fvIds.length],
    queryFn: async () => {
      if (fvIds.length === 0) return { rows: [] as AttendanceRow[] };
      // Parallel — server is local-network. N ≤ 100 is fine.
      const all = await Promise.all(
        fvIds.map((id) =>
          get<AttendanceListResponse>(`/frequent-visitors/${id}/attendance`, {
            params: { date_from: date, date_to: date, limit: 100 }
          }).then((r) => r.items)
        )
      );
      return { rows: all.flat() };
    },
    enabled: fvIds.length > 0,
    refetchOnWindowFocus: false
  });

  // Project to one row per FV × date.
  const projected = useMemo<ProjectedRow[]>(() => {
    const buckets = new Map<string, ProjectedRow>();
    for (const r of dayEvents.data?.rows ?? []) {
      const fv = fvMap.get(r.frequent_visitor_id);
      if (!fv) continue;
      let b = buckets.get(r.frequent_visitor_id);
      if (!b) {
        b = {
          fv_id: fv.id,
          fv_name: fv.full_name,
          fv_kind: fv.kind,
          unit_label: '',
          first_in: null,
          last_out: null,
          duration_minutes: null,
          events: 0
        };
        buckets.set(r.frequent_visitor_id, b);
      }
      b.events += 1;
      if (r.kind === 'in') {
        if (!b.first_in || new Date(r.ts).getTime() < new Date(b.first_in).getTime()) {
          b.first_in = r.ts;
        }
      } else {
        if (!b.last_out || new Date(r.ts).getTime() > new Date(b.last_out).getTime()) {
          b.last_out = r.ts;
        }
      }
    }
    for (const b of buckets.values()) {
      if (b.first_in && b.last_out) {
        b.duration_minutes = Math.max(
          0,
          Math.round((new Date(b.last_out).getTime() - new Date(b.first_in).getTime()) / 60000)
        );
      }
    }
    return Array.from(buckets.values());
  }, [dayEvents.data, fvMap]);

  // Filter / search.
  const visible = useMemo(() => {
    let rows = projected;
    if (kind) rows = rows.filter((r) => r.fv_kind === kind);
    const term = search.trim().toLowerCase();
    if (term) {
      rows = rows.filter((r) => r.fv_name.toLowerCase().includes(term));
    }
    return rows.sort((a, b) => a.fv_name.localeCompare(b.fv_name));
  }, [projected, kind, search]);

  // KPIs computed from raw event rows (not projected).
  const kpiIn = (dayEvents.data?.rows ?? []).filter((r) => r.kind === 'in').length;
  const kpiOut = (dayEvents.data?.rows ?? []).filter((r) => r.kind === 'out').length;
  const kpiUniqueFvs = new Set(
    (dayEvents.data?.rows ?? []).filter((r) => r.kind === 'in').map((r) => r.frequent_visitor_id)
  ).size;

  const onExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:4100';
      const apiPrefix = import.meta.env.VITE_API_PREFIX ?? '/v1';
      // Last 30 days, inclusive of today.
      const today = new Date();
      const thirtyAgo = new Date(today.getTime() - 30 * 24 * 3600 * 1000);
      const isoDay = (d: Date): string =>
        new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
      const params = new URLSearchParams({
        date_from: isoDay(thirtyAgo),
        date_to: isoDay(today)
      });

      const url = `${apiUrl}${apiPrefix}/fv-attendance/export.csv?${params.toString()}`;
      const token = useAuthStore.getState().accessToken;
      const res = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try {
          const body = (await res.json()) as { error?: { code?: string; message?: string } };
          if (body?.error?.message) msg = body.error.message;
        } catch {
          /* non-JSON body */
        }
        toast.error(msg);
        return;
      }

      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') ?? '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m?.[1] ?? `hooddesk-fv-attendance-${isoDay(today)}.csv`;

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      toast.success('Export downloaded');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const loading = fvList.isLoading || dayEvents.isLoading;

  return (
    <>
      <PageHeader
        title="Attendance"
        description="Daily IN/OUT log for verified domestic help."
        actions={
          <Button variant="secondary" size="sm" onClick={() => void onExportCsv()} disabled={exporting}>
            <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.75} />
            {exporting ? 'Exporting…' : 'Export CSV (last 30 days)'}
          </Button>
        }
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <LogIn className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Today's IN</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {loading ? <Skeleton className="h-7 w-12" /> : kpiIn}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-vt-warn">
                <LogOut className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Today's OUT</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {loading ? <Skeleton className="h-7 w-12" /> : kpiOut}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <Users className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Unique FVs today</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {loading ? <Skeleton className="h-7 w-12" /> : kpiUniqueFvs}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter row */}
        <Card>
          <CardContent className="flex flex-wrap items-end gap-vt-3 p-vt-4">
            <div className="flex items-center gap-vt-2 self-center">
              <Filter className="h-4 w-4 text-vt-text-muted" />
              <span className="text-vt-12 text-vt-text-muted">Filters:</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-vt-11 text-vt-text-muted">Date</span>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{ minWidth: 180 }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-vt-11 text-vt-text-muted">Kind</span>
              <Select
                value={kind}
                onChange={(e) => setKind(e.target.value as '' | FvKind)}
                style={{ minWidth: 160 }}
              >
                {KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-vt-11 text-vt-text-muted">Search name</span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-vt-text-muted" />
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter by name"
                  style={{ minWidth: 240, paddingLeft: 28 }}
                />
              </div>
            </div>
            {kind || search ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setKind('');
                  setSearch('');
                }}
              >
                Clear
              </Button>
            ) : null}
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {loading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : dayEvents.isError ? (
              <EmptyState
                icon={<ClipboardCheck className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load attendance"
                description={getErrorMessage(dayEvents.error)}
                action={
                  <Button variant="secondary" onClick={() => void dayEvents.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : visible.length === 0 ? (
              <EmptyState
                icon={<ClipboardCheck className="h-6 w-6" strokeWidth={1.75} />}
                title="No attendance on this date"
                description={
                  <span>
                    <CalendarDays className="mr-1 inline h-3.5 w-3.5 -translate-y-0.5" />
                    {formatDate(date)} · no IN/OUT events recorded by gate.
                  </span>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Frequent visitor</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>First IN</TableHead>
                    <TableHead>Last OUT</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Events</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((r) => (
                    <TableRow key={r.fv_id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-vt-text">{r.fv_name}</span>
                          <span className="font-mono text-vt-11 text-vt-text-muted">
                            {r.fv_id.slice(0, 8)}…
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="muted">{r.fv_kind}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-vt-text-muted">
                        {r.first_in ? formatDateTime(r.first_in) : '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-vt-text-muted">
                        {r.last_out ? formatDateTime(r.last_out) : '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-vt-text">
                        {r.duration_minutes !== null
                          ? `${Math.floor(r.duration_minutes / 60)}h ${r.duration_minutes % 60}m`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-vt-text-muted">{r.events}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {/* FV-list paginator — pages through the verified-FV cohort. The
                day-view above re-fans-out across the accumulated FV set on
                every load-more, so each click widens coverage. */}
            {!loading && !dayEvents.isError ? (
              <CursorPaginator
                hasMore={Boolean(fvPaginator.nextCursor)}
                loading={fvList.isFetching}
                itemsShown={fvItems.length}
                pageSize={FV_PAGE_SIZE}
                onLoadMore={() => {
                  if (fvPaginator.nextCursor) setCursor(fvPaginator.nextCursor);
                }}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
